import { PlainButton, theme, CloseIcon } from '@arnold/common';
import styled from '@emotion/styled';
import * as React from 'react';
import { ClassicPreset } from 'rete';

export class StatementControlData extends ClassicPreset.Control {
  constructor(
    public initialValue: string,
    public index: number,
    public key: string,
    public onChange: (value: string) => void,
    public onDelete: (saveState?: boolean) => void,
    public onMove: (from: number, to: number) => void,
    public readOnly = false,
    public showDeleteButton = true,
  ) {
    super();
  }
  initialValueChangedIndicator?: number;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  transition: all 0.2s ease-in-out;
  border: ${({ focused }: { focused: boolean }) =>
    `1px solid ${focused ? theme.colors.borderMain.active : theme.colors.backgroundBasic.default}`};
  border-radius: 5px !important;
  background-color: ${theme.colors.backgroundBasic.default};
  position: relative;

  & .textAreaSpan {
    flex: 1;
    background-color: transparent !important;
    outline: none;
    padding: 0 !important;
    min-height: 15px;
    margin: ${theme.spacing.d} ${theme.spacing.d} ${theme.spacing.d} 14px !important;
    border: none !important;
    border-radius: 0px !important;
    box-shadow: none !important;
  }
`;

const StyledPlainButton = styled(PlainButton)`
  background-color: transparent;
  padding: ${theme.spacing.b};
  width: ${theme.spacing.f};
  height: ${theme.spacing.f};
  margin-right: ${theme.spacing.d};

  & path {
    fill: ${theme.colors.iconText.secondary};
  }

  &:hover path {
    fill: ${theme.colors.iconAction.primary};
  }
`;

export const StatementControl = (props: { data: StatementControlData }) => {
  const [value, setValue] = React.useState(props.data.initialValue);
  const [focused, setFocused] = React.useState(false);

  React.useEffect(() => {
    if (value === props.data.initialValue) {
      // Workaround for contentEditable span not being directly controlled by React.
      // Since state and span value are not 2-way coupled, setting the same value won't trigger
      // a rerender. This forces 2 rerenders to ensure the value propagates to the span element.
      setValue(props.data.initialValue + ' ');
      setTimeout(() => {
        setValue(props.data.initialValue);
      }, 0);
    } else {
      setValue(props.data.initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data.initialValue, props.data.initialValueChangedIndicator]);

  return (
    <Container
      focused={focused}
      className="optionControl"
      data-icom="btn-statement-ariel"
      data-cy="btn-statement-ariel"
    >
      <span
        className="textAreaSpan"
        contentEditable={!props.data.readOnly}
        data-cy="statement-txt-input"
        suppressContentEditableWarning={true}
        role="textbox"
        onInput={(e) => {
          const val = (e.target as HTMLElement).innerText;
          props.data.onChange(val);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertHTML', false, text);
        }}
      >
        {value}
      </span>
      {!props.data.readOnly && props.data.showDeleteButton && (
        <StyledPlainButton
          onClick={() => props.data.onDelete()}
          data-icom={'btn-remove-statement-ariel'}
          data-cy="btn-remove-statement-ariel"
          className="deleteOptionButton"
        >
          <CloseIcon />
        </StyledPlainButton>
      )}
    </Container>
  );
};
